// third-party
import { generate, presetDarkPalettes, presetPalettes } from '@ant-design/colors';

// project imports
import ThemeOption from './theme';
import { extendPaletteWithChannels, withAlpha } from 'utils/colorUtils';

const greyAscent = ['#fafafa', '#bfbfbf', '#434343', '#1f1f1f'];

// ==============================|| GREY COLORS BUILDER ||============================== //

function buildGrey(mode: 'light' | 'dark') {
  let greyPrimary = [
    '#ffffff',
    '#fafafa',
    '#f5f5f5',
    '#f0f0f0',
    '#d9d9d9',
    '#bfbfbf',
    '#8c8c8c',
    '#595959',
    '#262626',
    '#141414',
    '#000000'
  ];
  let greyConstant = ['#fafafb', '#e6ebf1'];

  if (mode === 'dark') {
    greyPrimary = ['#000000', '#141414', '#1e1e1e', '#595959', '#8c8c8c', '#bfbfbf', '#d9d9d9', '#f0f0f0', '#f5f5f5', '#fafafa', '#ffffff'];
    greyConstant = ['#121212', '#d3d8db'];
  }

  return [...greyPrimary, ...greyAscent, ...greyConstant];
}

// ==============================|| DEFAULT THEME - PALETTE ||============================== //

export function buildPalette(presetColor: string) {
  void presetColor;

  // Boneside light: primário slate-blue (#5D7489 do IDV) no lugar do azul padrão do template.
  // Ramp claro afinado à mão — o generate() do Ant dessatura o topo em cinzas "sujos" p/ esta
  // cor de baixa saturação; aqui os tons claros ficam limpos p/ hover/seleção e as âncoras
  // light/main/dark batem exatamente na marca (texto branco sobre main passa AA).
  const slateRampLight = [
    '#ECEFF2', // lighter — tint p/ hover / selected em fundo claro
    '#D7DDE3', // 100
    '#B9C3CC', // 200
    '#93A3B2', // light — borda de foco / hover de input
    '#74899C', // 400
    '#5D7489', // main — cor oficial da marca
    '#46586A', // dark — hover de botão, outline de foco, cor de link
    '#374757', // 700
    '#283643', // darker
    '#131D26' // 900 — navy mais escuro da marca
  ];
  const lightColors = { ...presetPalettes, blue: slateRampLight, grey: buildGrey('light') };
  const lightPaletteColor = ThemeOption(lightColors);

  // Boneside dark: primário slate-blue (#5D7489 do IDV). Usa o ramp dark-aware (tons lighter/darker
  // coerentes p/ fundos e seleção) e ancora light/main nas cores exatas da marca.
  const slateRamp = generate('#5D7489', { theme: 'dark', backgroundColor: '#131D26' });
  slateRamp[3] = '#7E94A8'; // primary.light
  slateRamp[5] = '#5D7489'; // primary.main (cor oficial — texto branco passa AA)
  // primary.dark é o tom de hover/foco/link. No dark ele precisa ser MAIS CLARO que o main (é a
  // convenção dos ramps escuros do Ant): com o #46586A da marca o texto ficava em 2:1 sobre o
  // fundo escuro e sumia no hover. Slate claro da própria marca, ~5.9:1 sobre o paper.
  slateRamp[6] = '#93A3B2'; // primary.dark
  const darkColors = { ...presetDarkPalettes, blue: slateRamp, grey: buildGrey('dark') };
  const darkPaletteColor = ThemeOption(darkColors);

  const commonColor = { common: { black: '#000', white: '#fff' } };

  const extendedLight = extendPaletteWithChannels(lightPaletteColor);
  const extendedDark = extendPaletteWithChannels(darkPaletteColor);
  const extendedCommon = extendPaletteWithChannels(commonColor);

  return {
    light: {
      mode: 'light',
      ...extendedCommon,
      ...extendedLight,
      // text.secondary em grey[600] (#595959, ~7:1 sobre o branco). O grey[500] (#8c8c8c) dava
      // 3,4:1 e reprovava o AA (4,5:1) em texto pequeno.
      text: {
        primary: extendedLight.grey[700],
        secondary: extendedLight.grey[600],
        disabled: extendedLight.grey[400]
      },
      action: { disabled: extendedLight.grey[300] },
      divider: extendedLight.grey[200],
      background: {
        paper: extendedLight.grey[0],
        default: extendedLight.grey.A50
      }
    },
    dark: {
      mode: 'dark',
      ...extendedCommon,
      ...extendedDark,
      // Boneside (IDV): texto #FCFCFC/#B1B1B1, divisor #55626E e fundos navy.
      text: {
        primary: '#FCFCFC',
        secondary: '#B1B1B1',
        disabled: withAlpha(extendedDark.grey[900], 0.35)
      },
      action: { disabled: extendedDark.grey[300] },
      divider: 'rgba(85, 98, 110, 0.45)',
      background: {
        paper: '#1B2731',
        default: '#131D26'
      }
    }
  };
}
